import {
  Box,
  Drawer,
  styled,
  useMediaQuery,
  Theme,
  alpha,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { FC, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Close as CloseIcon } from "@mui/icons-material";
import { IsSuperUser } from "../../utils/permissions";
import {
  MenuSections,
  IMenuItem,
} from "./MenuList";
import { LogoSVG } from "../../assets/svg/SvgIcons";

interface SideNavBarProps {
  showMobileSideBar: boolean;
  closeMobileSideBar: () => void;
}

/* ------------------------------------------------------------------ */
/* Styled components (desktop sidebar — unchanged)                    */
/* ------------------------------------------------------------------ */

const MainMenu = styled(Box)(({ theme }) => ({
  left: 0,
  width: 190,
  height: "100%",
  position: "fixed",
  boxShadow: theme.shadows[2],
  transition: "left 0.3s ease",
  zIndex: theme.zIndex.drawer + 11,
  backgroundColor: theme.palette.primary.main,
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": { width: 0 },
  [theme.breakpoints.down("md")]: { display: "none" },
}));

const LogoImage = styled("img")(() => ({
  maxHeight: 28,
  maxWidth: 90,
  objectFit: "contain",
}));

const NavItem = styled(Box, {
  shouldForwardProp: (p) => p !== "isActive",
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  padding: "9px 16px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
  borderRadius: 8,
  margin: "2px 8px",
  transition: "all 0.15s ease",
  color: isActive ? "#fff" : "rgba(255, 255, 255, 0.65)",
  backgroundColor: isActive ? alpha("#fff", 0.15) : "transparent",
  fontWeight: isActive ? 700 : 500,
  "&:hover": {
    backgroundColor: alpha("#fff", isActive ? 0.15 : 0.08),
    color: "#fff",
  },
}));

const SectionLabel = styled(Typography)(() => ({
  fontSize: 9,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 1.2,
  color: "rgba(255, 255, 255, 0.35)",
  padding: "12px 16px 4px 16px",
  userSelect: "none",
}));

/* ------------------------------------------------------------------ */
/* Shared nav content (used in both desktop and mobile drawer)       */
/* ------------------------------------------------------------------ */

const NavContent: FC<{
  sections: ReturnType<typeof MenuSections>;
  isActive: (path: string) => boolean;
  onNav: (item: IMenuItem) => () => void;
  onClose?: () => void;
  isMobile?: boolean;
}> = ({ sections, isActive, onNav, onClose, isMobile }) => (
  <Box
    sx={{
      width: isMobile ? 270 : "100%",
      height: "100%",
      bgcolor: "primary.main",
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Header */}
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          bgcolor: alpha("#fff", 0.15),
          borderRadius: 2,
          px: 1.5,
          py: 1,
          border: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
        }}
      >
        {isMobile ? (
          <LogoSVG width={80} height={24} />
        ) : (
          <LogoImage src="/Everpesa3.png" alt="Everpesa" />
        )}
      </Box>

      {/* Close button — mobile only */}
      {isMobile && onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "#fff", bgcolor: alpha("#fff", 0.1) },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
    </Box>

    {/* Scrollable nav area */}
    <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", "&::-webkit-scrollbar": { width: 0 } }}>
      {sections.map((section, sIdx) => (
        <Box key={sIdx}>
          {section.section && (
            <>
              {sIdx > 0 && (
                <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2, my: 0.5 }} />
              )}
              <SectionLabel>{section.section}</SectionLabel>
            </>
          )}
          {section.items
            .filter((item) => item.visible)
            .map((nav, nIdx) => (
              <NavItem key={nIdx} isActive={isActive(nav.path)} onClick={onNav(nav)}>
                <nav.Icon sx={{ fontSize: 18, flexShrink: 0 }} />
                <Typography
                  sx={{ fontSize: 12, fontWeight: "inherit", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {nav.title}
                </Typography>
              </NavItem>
            ))}
        </Box>
      ))}
      <Box sx={{ height: 24 }} />
    </Box>
  </Box>
);

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

const DashboardSideBar: FC<SideNavBarProps> = ({
  showMobileSideBar,
  closeMobileSideBar,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const isSuperUser = IsSuperUser();
  const sections = useMemo(() => MenuSections(isSuperUser), [isSuperUser]);

  const isActive = (path: string) => {
    if (path === "/dashboard")
      return location.pathname === "/dashboard" || location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item: IMenuItem) => () => {
    navigate(item.path);
    closeMobileSideBar();
  };

  return (
    <>
      {/* ── Desktop Fixed Sidebar ── */}
      {!downMd && (
        <MainMenu>
          <NavContent
            sections={sections}
            isActive={isActive}
            onNav={handleNavClick}
          />
        </MainMenu>
      )}

      {/* ── Mobile Slide-in Drawer ── */}
      {downMd && (
        <Drawer
          anchor="left"
          open={showMobileSideBar}
          onClose={closeMobileSideBar}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: 270,
              bgcolor: "primary.main",
              border: "none",
              boxShadow: 8,
            },
          }}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 20,
            "& .MuiBackdrop-root": {
              backdropFilter: "blur(2px)",
              bgcolor: "rgba(0,0,0,0.45)",
            },
          }}
        >
          <NavContent
            sections={sections}
            isActive={isActive}
            onNav={handleNavClick}
            onClose={closeMobileSideBar}
            isMobile
          />
        </Drawer>
      )}
    </>
  );
};

export default DashboardSideBar;